import type { ConversaDetalhe } from '@mymaxx/shared';
import {
  BadgeCheck,
  ClipboardList,
  FileText,
  Plus,
  Shield,
  Star,
  StickyNote,
  UserSearch,
  Wifi,
} from 'lucide-react';
import { useState } from 'react';
import { ErroApi } from '../lib/api';
import { atendimentoApi } from '../lib/atendimento-api';

type Aba = 'perfil' | 'historico';

/**
 * Coluna da direita: quem é o cliente e o histórico dele. Clicar num protocolo anterior
 * abre aquela conversa no lugar do chat atual — é assim que o atendente lembra o que já
 * foi tratado sem sair do atendimento.
 */
export function PainelCliente({
  conversa,
  anteriorAberto,
  onAbrirAnterior,
  onVincular,
  onAnotou,
}: {
  conversa: ConversaDetalhe;
  /** Protocolo anterior que está ocupando o centro da tela, se houver. */
  anteriorAberto?: string | null;
  onAbrirAnterior?: (conversaId: string) => void;
  /** Abre a busca de cadastro quando o atendimento ainda está sem identificação. */
  onVincular?: () => void;
  /** Recarrega a conversa depois de uma nota escrita aqui. Sem ele, a ficha é só leitura. */
  onAnotou?: () => void;
}) {
  const [aba, setAba] = useState<Aba>('perfil');
  const { dadosCliente: cliente } = conversa;

  return (
    <aside className="hidden w-80 shrink-0 flex-col border-l border-neutral-200 lg:flex dark:border-neutral-800">
      <div className="flex border-b border-neutral-200 text-xs dark:border-neutral-800">
        {(
          [
            ['perfil', 'Perfil'],
            [
              'historico',
              `Histórico${cliente.historico.length ? ` (${cliente.historico.length})` : ''}`,
            ],
          ] as const
        ).map(([valor, rotulo]) => (
          <button
            key={valor}
            type="button"
            onClick={() => setAba(valor)}
            className={`flex-1 px-2 py-2.5 transition ${
              aba === valor
                ? 'border-b-2 border-marca-600 font-medium text-marca-700 dark:text-marca-300'
                : 'text-neutral-500 hover:bg-neutral-50 dark:hover:bg-neutral-900'
            }`}
          >
            {rotulo}
          </button>
        ))}
      </div>

      <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-4">
        {aba === 'perfil' ? (
          <Perfil conversa={conversa} onVincular={onVincular} onAnotou={onAnotou} />
        ) : (
          <Historico
            conversa={conversa}
            anteriorAberto={anteriorAberto ?? null}
            onAbrir={onAbrirAnterior}
          />
        )}
      </div>
    </aside>
  );
}

function Perfil({
  conversa,
  onVincular,
  onAnotou,
}: {
  conversa: ConversaDetalhe;
  onVincular?: () => void;
  onAnotou?: () => void;
}) {
  const { dadosCliente: cliente } = conversa;
  const semCadastro = !cliente.clienteId;

  return (
    <>
      <div>
        <p className="flex items-center gap-1.5 text-sm font-medium">
          {cliente.nivelIdentidade === 'verificado' ? (
            <BadgeCheck size={15} className="text-green-600" />
          ) : (
            <Shield size={15} className="text-neutral-400" />
          )}
          {cliente.nome ?? 'Ainda não identificado'}
        </p>
        <p className="text-xs text-neutral-500">
          {cliente.nivelIdentidade === 'verificado'
            ? 'Identidade verificada'
            : 'Visitante (sem login)'}
        </p>
      </div>

      {/* A triagem não achou o cadastro: o atendente procura e vincula na mão. */}
      {semCadastro && onVincular && (
        <button
          type="button"
          onClick={onVincular}
          className="flex w-full items-center justify-center gap-1.5 rounded-lg border border-dashed border-marca-400 px-3 py-2 text-xs text-marca-700 transition hover:bg-marca-50 dark:border-marca-700 dark:text-marca-300 dark:hover:bg-marca-950"
        >
          <UserSearch size={14} /> Vincular a um cadastro
        </button>
      )}

      <dl className="space-y-1.5 text-xs">
        <Campo rotulo="Documento" valor={cliente.documento} />
        <Campo rotulo="Telefone" valor={cliente.telefone} />
        <Campo rotulo="E-mail" valor={cliente.email} />
      </dl>

      {/* Logo abaixo dos dados de quem é: a ficha vem antes do atendimento em si, porque é
          o que o atendente precisa ler ANTES de falar. */}
      <NotasDoCliente conversa={conversa} onAnotou={onAnotou} />

      <ResumoDoAtendimento conversa={conversa} />

      {cliente.pontoServico && (
        <div className="rounded-lg border border-neutral-200 p-2 dark:border-neutral-700">
          <p className="flex items-center gap-1.5 text-xs font-medium">
            <Wifi size={13} /> Ponto de serviço
          </p>
          <p className="mt-1 text-xs text-neutral-600 dark:text-neutral-300">
            {cliente.pontoServico.rotulo}
          </p>
          <p className="text-[11px] text-neutral-500">
            {cliente.pontoServico.plano ?? 'Plano não informado'}
            {cliente.pontoServico.status ? ` · ${cliente.pontoServico.status}` : ''}
          </p>
        </div>
      )}

      {conversa.transferencias.length > 0 && (
        <div>
          <p className="mb-1 text-xs font-medium">Transferências</p>
          <ul className="space-y-1 text-[11px] text-neutral-500">
            {conversa.transferencias.map((t, i) => (
              <li key={i}>
                {t.de ?? '—'} → {t.para ?? '—'}
                {t.motivo ? ` · ${t.motivo}` : ''}
              </li>
            ))}
          </ul>
        </div>
      )}
    </>
  );
}

/**
 * O atendimento em si, ao lado do fio.
 *
 * Existe porque quem abre um protocolo encerrado quer saber o desfecho sem ler tudo de
 * novo: quem atendeu, quanto durou, por que fechou e que nota saiu de lá. Numa conversa em
 * andamento, o mesmo bloco responde "há quanto tempo isto está aberto".
 *
 * As duas notas têm sentidos opostos e por isso nunca dividem rótulo: `avaliacao` é a nota
 * que quem foi atendido deu ao atendente, e `avaliacaoTecnico` é a nota que o atendente deu
 * ao técnico na liberação. Chamar as duas de "avaliação" já confundiu gente o bastante.
 */
function ResumoDoAtendimento({ conversa }: { conversa: ConversaDetalhe }) {
  const encerrada = conversa.status === 'encerrada';
  const quemFoiAtendido = conversa.clienteTecnico ? 'o técnico' : 'o cliente';

  return (
    <div className="rounded-lg border border-neutral-200 p-2 dark:border-neutral-700">
      <p className="mb-1.5 flex items-center gap-1.5 text-xs font-medium">
        <ClipboardList size={13} /> Este atendimento
      </p>
      <dl className="space-y-1.5 text-xs">
        <Campo rotulo="Protocolo" valor={conversa.protocolo} />
        <Campo rotulo="Setor" valor={conversa.setor?.nome ?? null} />
        <Campo rotulo="Atendente" valor={conversa.atendente?.nome ?? null} />
        <Campo rotulo="Origem" valor={conversa.origem === 'app' ? 'Aplicativo' : 'Site'} />
        <Campo rotulo="Início" valor={new Date(conversa.iniciadaEm).toLocaleString('pt-BR')} />
        {encerrada && (
          <>
            <Campo
              rotulo="Encerrado"
              valor={
                conversa.encerradaEm ? new Date(conversa.encerradaEm).toLocaleString('pt-BR') : null
              }
            />
            <Campo rotulo="Duração" valor={duracao(conversa.duracaoSeg)} />
            <Campo rotulo="Motivo" valor={conversa.motivo} />
          </>
        )}
      </dl>

      {(conversa.avaliacao !== null || conversa.avaliacaoTecnico !== null) && (
        <div className="mt-2 space-y-1.5 border-t border-neutral-200 pt-2 dark:border-neutral-700">
          {conversa.avaliacao !== null && (
            <Nota
              rotulo={`Nota que ${quemFoiAtendido} deu`}
              valor={conversa.avaliacao}
              comentario={conversa.avaliacaoComentario}
            />
          )}
          {conversa.avaliacaoTecnico !== null && (
            <Nota rotulo="Nota que o atendente deu ao técnico" valor={conversa.avaliacaoTecnico} />
          )}
        </div>
      )}

      {encerrada && conversa.avaliacao === null && (
        <p className="mt-2 border-t border-neutral-200 pt-2 text-[11px] text-neutral-500 dark:border-neutral-700">
          Sem nota — encerramentos por inatividade e motivos dispensados não pedem avaliação.
        </p>
      )}
    </div>
  );
}

/**
 * A ficha do cliente: tudo que a equipe já anotou sobre ele, aqui e nos atendimentos
 * anteriores.
 *
 * Fica ao lado do perfil, e não só dentro do fio, porque é assim que a nota é usada: o
 * atendente abre o atendimento e quer saber, antes de falar, o que já aconteceu com esta
 * pessoa — "já prometeram visita", "não aceita agendamento de manhã". Dentro do fio ela
 * só existia enquanto aquele protocolo estivesse aberto.
 *
 * O cliente nunca vê nada disto: nota interna não entra no chat dele.
 */
function NotasDoCliente({
  conversa,
  onAnotou,
}: {
  conversa: ConversaDetalhe;
  onAnotou?: () => void;
}) {
  const notas = conversa.dadosCliente.notas;
  const [escrevendo, setEscrevendo] = useState(false);
  const [texto, setTexto] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  // Só quem está dentro do atendimento escreve. Quem passa por ali para olhar (supervisão,
  // fila) lê a ficha, e é o suficiente.
  const podeEscrever = !!onAnotou && !!conversa.meuPapel && conversa.status !== 'encerrada';

  async function salvar() {
    const conteudo = texto.trim();
    if (!conteudo || salvando) return;
    setSalvando(true);
    setErro(null);
    try {
      await atendimentoApi.anotar(conversa.id, conteudo);
      setTexto('');
      setEscrevendo(false);
      onAnotou?.();
    } catch (e) {
      setErro(e instanceof ErroApi ? e.mensagem : 'Não consegui salvar a nota');
    } finally {
      setSalvando(false);
    }
  }

  return (
    <div className="rounded-lg border border-neutral-200 p-2 dark:border-neutral-700">
      <div className="mb-1.5 flex items-center justify-between gap-2">
        <p className="flex items-center gap-1.5 text-xs font-medium">
          <StickyNote size={13} /> Notas internas
          {notas.length > 0 && <span className="text-neutral-500">({notas.length})</span>}
        </p>
        {podeEscrever && !escrevendo && (
          <button
            type="button"
            onClick={() => setEscrevendo(true)}
            className="flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] text-marca-700 transition hover:bg-marca-50 dark:text-marca-300 dark:hover:bg-marca-950"
          >
            <Plus size={12} /> Anotar
          </button>
        )}
      </div>

      {escrevendo && (
        <div className="mb-2 space-y-1">
          <textarea
            rows={3}
            autoFocus
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) void salvar();
              if (e.key === 'Escape') setEscrevendo(false);
            }}
            placeholder="O que a próxima pessoa precisa saber…"
            className="w-full resize-none rounded border border-amber-300 bg-amber-50 px-2 py-1.5 text-xs dark:border-amber-800 dark:bg-amber-950/40"
          />
          <div className="flex justify-end gap-1">
            <button
              type="button"
              onClick={() => {
                setEscrevendo(false);
                setTexto('');
              }}
              className="rounded px-2 py-1 text-[11px] text-neutral-500 hover:bg-neutral-100 dark:hover:bg-neutral-800"
            >
              Cancelar
            </button>
            <button
              type="button"
              disabled={salvando || !texto.trim()}
              onClick={() => void salvar()}
              className="rounded bg-marca-600 px-2 py-1 text-[11px] text-white transition hover:bg-marca-700 disabled:opacity-50"
            >
              {salvando ? 'Salvando…' : 'Salvar'}
            </button>
          </div>
        </div>
      )}

      {erro && <p className="mb-1 text-[11px] text-red-600">{erro}</p>}

      {notas.length === 0 ? (
        <p className="text-[11px] text-neutral-500">
          Nada anotado sobre este cliente ainda.
          {podeEscrever ? ' O que você escrever aqui fica para os próximos atendimentos.' : ''}
        </p>
      ) : (
        <ul className="max-h-64 space-y-1.5 overflow-y-auto">
          {notas.map((n) => (
            <li
              key={n.id}
              className="rounded border border-amber-200 bg-amber-50 px-2 py-1.5 dark:border-amber-900 dark:bg-amber-950/30"
            >
              <p className="text-xs whitespace-pre-line">{n.texto}</p>
              <p className="mt-0.5 text-[10px] text-neutral-500">
                {n.autorNome ?? 'Equipe'} · {new Date(n.criadaEm).toLocaleString('pt-BR')}
                {/* De qual atendimento veio: sem isso, uma nota de três meses atrás parece
                    ser sobre o caso de agora. */}
                {!n.desteAtendimento && ` · ${n.protocolo}`}
              </p>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Nota({
  rotulo,
  valor,
  comentario,
}: {
  rotulo: string;
  valor: number;
  comentario?: string | null;
}) {
  return (
    <div>
      <p className="flex items-center gap-1 text-[11px] text-neutral-500">
        {rotulo}
        <span className="flex items-center gap-0.5 font-medium text-amber-600 dark:text-amber-400">
          {valor}
          <Star size={11} fill="currentColor" />
        </span>
      </p>
      {comentario && (
        <p className="mt-0.5 text-xs text-neutral-600 italic dark:text-neutral-300">
          “{comentario}”
        </p>
      )}
    </div>
  );
}

/** Segundos viram "1h 12min" — quem lê quer a ordem de grandeza, não o cronômetro. */
function duracao(segundos: number | null): string | null {
  if (segundos === null) return null;
  const min = Math.round(segundos / 60);
  if (min < 60) return `${min} min`;
  return `${Math.floor(min / 60)}h ${String(min % 60).padStart(2, '0')}min`;
}

function Historico({
  conversa,
  anteriorAberto,
  onAbrir,
}: {
  conversa: ConversaDetalhe;
  anteriorAberto: string | null;
  onAbrir?: (conversaId: string) => void;
}) {
  const historico = conversa.dadosCliente.historico;

  if (historico.length === 0) {
    return (
      <p className="py-6 text-center text-xs text-neutral-500">
        Primeiro contato deste cliente. Não há protocolos anteriores.
      </p>
    );
  }

  return (
    <div>
      <p className="mb-2 flex items-center gap-1.5 text-xs font-medium">
        <FileText size={13} /> Atendimentos anteriores
      </p>
      <ul className="space-y-1">
        {historico.map((h) => {
          const aberto = anteriorAberto === h.id;
          return (
            <li key={h.id}>
              <button
                type="button"
                disabled={!onAbrir}
                onClick={() => onAbrir?.(h.id)}
                className={`w-full rounded-lg border px-2 py-1.5 text-left transition disabled:cursor-default ${
                  aberto
                    ? 'border-marca-500 bg-marca-50 dark:bg-marca-950'
                    : 'border-neutral-200 enabled:hover:bg-neutral-50 dark:border-neutral-800 dark:enabled:hover:bg-neutral-900'
                }`}
              >
                <span className="block font-mono text-[11px]">{h.protocolo}</span>
                <span className="block text-[11px] text-neutral-500">
                  {h.setor ?? 'sem setor'}
                  {h.encerradaEm
                    ? ` · ${new Date(h.encerradaEm).toLocaleDateString('pt-BR')}`
                    : ' · em aberto'}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
      {onAbrir && (
        <p className="mt-2 text-[11px] text-neutral-500">
          Clique num protocolo para ler a conversa. Ela abre no lugar do chat atual.
        </p>
      )}
    </div>
  );
}

function Campo({ rotulo, valor }: { rotulo: string; valor: string | null }) {
  return (
    <div className="flex justify-between gap-2">
      <dt className="text-neutral-500">{rotulo}</dt>
      <dd className="truncate text-right">{valor ?? '—'}</dd>
    </div>
  );
}
